<template>
  <div class="home">
    <div class="hero" ref="heroContainer">
      <el-carousel ref="heroCarousel" :interval="5000" arrow="never" indicator-position="none" direction="vertical" @change="onSlideChange">
        <el-carousel-item v-for="(slide, i) in slides" :key="i">
          <!-- 移动端时最后一项为 footer，PC 端由 layout 展示 -->
          <div v-if="slide.isFooter" class="hero-footer-slide">
            <div v-if="lastSlideImage" class="hero-footer-last-img" :style="{ backgroundImage: `url(${lastSlideImage})` }"></div>
            <div class="footer footer-in-carousel">
              <div class="footer-inner">
                <div class="footer-left">
                  <div v-if="footerRemarkHtml" class="footer-remark" v-html="footerRemarkHtml"></div>
                  <p v-if="authInfo" class="copyright">{{ authInfo }}</p>
                </div>
                <div class="footer-right">
                  <div v-for="(qr, index) in qrCodes" :key="index" class="qr-item">
                    <div class="qr-box"><img :src="qr" alt="二维码" @error="($event.target.style.display='none')"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="hero-bg" :style="{ backgroundImage: `url(${slide.image})` }" />
        </el-carousel-item>
      </el-carousel>

      <div v-show="!isFooterSlide" class="hero-overlay">
        <div :key="currentIndex" class="hero-overlay-inner">
          <template v-if="currentSlide && (currentSlide.title || currentSlide.description)">
            <h1 v-if="currentSlide.title" class="hero-title hero-fly-left">{{ currentSlide.title }}</h1>
            <p v-if="currentSlide.description" class="hero-title hero-fly-right">{{ currentSlide.description }}</p>
          </template>
          <template v-else>
            <h1 class="hero-title hero-fly-left">开启线上线下一体化</h1>
            <h1 class="hero-title hero-fly-right">水果即时零售新风口</h1>
          </template>
        </div>
      </div>

      <div class="hero-dots">
        <span v-for="(_, i) in slides" :key="i" class="dot" :class="{ active: i === currentIndex }" @click="goToSlide(i)" />
      </div>
    </div>
  </div>
</template>

<script>
import { getMenuHtmlDetail } from '@/api/menu'

const DEFAULT_SLIDES = [
  { image: '/images/hero-1.jpg' },
  { image: '/images/hero-2.jpg' }
]

export default {
  name: 'HomePage',
  data () {
    return {
      currentIndex: 0,
      rawSlides: [...DEFAULT_SLIDES],
      wheelTimer: null,
      touchStartY: 0
    }
  },
  computed: {
    // PC 和移动端都显示 footer 轮播项；页脚数据与 layout 共用 Vuex globalConfig
    slides () {
      return [...this.rawSlides, { isFooter: true }]
    },
    footerRemarkHtml () {
      return this.$store.getters.globalConfigRemarkInfo
    },
    authInfo () {
      return this.$store.getters.globalConfigAuthInfo
    },
    qrCodes () {
      return this.$store.getters.globalConfigQrCodes
    },
    currentSlide () {
      return this.slides[this.currentIndex] || null
    },
    isFooterSlide () {
      const slide = this.currentSlide
      return slide && slide.isFooter === true
    },
    /** 最后一张轮播图（footer 上方展示用） */
    lastSlideImage () {
      if (!this.rawSlides.length) return ''
      const last = this.rawSlides[this.rawSlides.length - 1]
      return (last && last.image) || ''
    }
  },
  mounted () {
    this.fetchBanners()
    this.addWheelListener()
    this.addTouchListener()
  },
  beforeDestroy () {
    this.removeWheelListener()
    this.removeTouchListener()
  },
  methods: {
    // 首页轮播图：进入页面时请求接口
    async fetchBanners () {
      try {
        const res = await getMenuHtmlDetail({ webMenuId: '1' })
        if (res && res.result && res.result.htmlContent) {
          const htmlContent = res.result.htmlContent
          const config = typeof htmlContent === 'string' ? JSON.parse(htmlContent) : htmlContent
          if (Array.isArray(config.banners) && config.banners.length) {
            const list = config.banners.map(b => ({
              image: b.image || '',
              title: b.title || '',
              description: b.description || ''
            })).filter(b => b.image)
            if (list.length) this.rawSlides = list
          }
          this.$store.commit('SET_GLOBAL_CONFIG', config)
        }
      } catch (e) {
        console.warn('首页轮播配置请求失败:', e)
      }
    },
    onSlideChange (index) {
      this.currentIndex = index
    },
    goToSlide (i) {
      this.currentIndex = i
      const carousel = this.$refs.heroCarousel
      if (carousel && carousel.setActiveItem) {
        carousel.setActiveItem(i)
      }
    },
    handleWheel (e) {
      // 清除之前的定时器
      if (this.wheelTimer) {
        clearTimeout(this.wheelTimer)
      }

      // 防抖：300ms 内只响应一次
      this.wheelTimer = setTimeout(() => {
        const delta = e.deltaY || e.detail || -e.wheelDelta

        if (delta > 0) {
          // 向下滚动，切换到下一张
          this.nextSlide()
        } else {
          // 向上滚动，切换到上一张
          this.prevSlide()
        }
      }, 300)
    },
    nextSlide () {
      const nextIndex = (this.currentIndex + 1) % this.slides.length
      this.goToSlide(nextIndex)
    },
    prevSlide () {
      const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length
      this.goToSlide(prevIndex)
    },
    addWheelListener () {
      const container = this.$refs.heroContainer
      if (container) {
        // 兼容不同浏览器
        container.addEventListener('wheel', this.handleWheel, { passive: false })
        container.addEventListener('mousewheel', this.handleWheel, { passive: false })
        container.addEventListener('DOMMouseScroll', this.handleWheel, { passive: false })
      }
    },
    removeWheelListener () {
      const container = this.$refs.heroContainer
      if (container) {
        container.removeEventListener('wheel', this.handleWheel)
        container.removeEventListener('mousewheel', this.handleWheel)
        container.removeEventListener('DOMMouseScroll', this.handleWheel)
      }
      if (this.wheelTimer) {
        clearTimeout(this.wheelTimer)
      }
    },
    handleTouchStart (e) {
      this.touchStartY = e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY
    },
    handleTouchEnd (e) {
      const endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : e.clientY
      const delta = this.touchStartY - endY
      const threshold = 50
      if (delta > threshold) {
        this.nextSlide()
      } else if (delta < -threshold) {
        this.prevSlide()
      }
    },
    addTouchListener () {
      const container = this.$refs.heroContainer
      if (container) {
        container.addEventListener('touchstart', this.handleTouchStart, { passive: true })
        container.addEventListener('touchend', this.handleTouchEnd, { passive: true })
      }
    },
    removeTouchListener () {
      const container = this.$refs.heroContainer
      if (container) {
        container.removeEventListener('touchstart', this.handleTouchStart)
        container.removeEventListener('touchend', this.handleTouchEnd)
      }
    }
  }
}
</script>

<style scoped>
.home {
  padding: 0;
  margin: 0;
}

/* 图片铺满剩余空间：高度 = 视口 - header（PC 64px） */
.hero {
  position: relative;
  width: 100%;
  min-height: calc(100vh - 64px);
  height: calc(100vh - 64px);
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1a472a;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

/* 无图时的渐变遮罩与 fallback */
.hero-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.35) 100%
  );
  pointer-events: none;
}

.hero-overlay {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  text-align: center;
  padding: 0 24px;
  z-index: 2;
}

.hero-overlay-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

/* 切换时飞入：从左飞入中间，距离 200px */
.hero-fly-left {
  animation: heroFlyFromLeft 0.5s ease-out forwards;
}

/* 切换时飞入：从右飞入中间，距离 200px */
.hero-fly-right {
  animation: heroFlyFromRight 0.5s ease-out forwards;
}

@keyframes heroFlyFromLeft {
  from {
    transform: translateX(-200px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes heroFlyFromRight {
  from {
    transform: translateX(200px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.hero-title {
  margin: 0 0 12px;
  font-size: 95px;
  font-weight: bold;
  line-height: 1;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.hero-title:last-child {
  margin-bottom: 0;
}

.hero-dots {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 2;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s;
}

.dot:hover,
.dot.active {
  background: #fff;
  transform: scale(1.2);
}

/* 覆盖 el-carousel 默认样式，铺满 hero 区域 */
.hero ::v-deep .el-carousel__container {
  height: calc(100vh - 64px) !important;
  min-height: calc(100vh - 64px);
}
.hero ::v-deep .el-carousel__item {
  height: calc(100vh - 64px);
  background: transparent;
}

/* 轮播内 footer：上方最后一张图，下方与 layout 一致 */
.hero-footer-slide {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background: #1a472a;
  color: #fff;
}
.hero-footer-last-img {
  width: 100%;
  height: 0;
  flex: 1;
  flex-shrink: 0;
  background-size: 100% auto;
  background-position: center bottom;
  background-repeat: no-repeat;
}
.hero-footer-slide .footer.footer-in-carousel {
  width: 100%;
  padding: 40px 20px 24px;
  box-sizing: border-box;
}
.hero-footer-slide .footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 32px;
}
.hero-footer-slide .footer-left p,
.hero-footer-slide .footer-remark ::v-deep p {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.6;
}
.hero-footer-slide .footer-remark ::v-deep a {
  color: #fff;
  text-decoration: none;
}
.hero-footer-slide .footer-remark ::v-deep a:hover {
  text-decoration: underline;
}
.hero-footer-slide .footer-left a {
  color: #fff;
  text-decoration: none;
}
.hero-footer-slide .footer-left a:hover {
  text-decoration: underline;
}
.hero-footer-slide .copyright {
  margin-top: 12px;
  font-size: 12px;
  opacity: 0.9;
}
.hero-footer-slide .footer-right {
  display: flex;
  gap: 24px;
}
.hero-footer-slide .qr-item {
  text-align: center;
  font-size: 12px;
}
.hero-footer-slide .qr-box {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hero-footer-slide .qr-box img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 移动端：图片撑满屏幕（减去 header 高度 56px） */
@media (max-width: 768px) {
  .hero {
    min-height: calc(100vh - 56px);
    height: calc(100vh - 56px);
  }
  .hero ::v-deep .el-carousel__container {
    height: calc(100vh - 56px) !important;
    min-height: calc(100vh - 56px);
  }
  .hero ::v-deep .el-carousel__item {
    height: calc(100vh - 56px);
  }
  .hero-bg {
    background-size: cover;
    background-position: center;
  }
  .hero-title {
    font-size: 36px;
  }
  .hero-dots {
    right: 16px;
    gap: 10px;
  }
  .dot {
    width: 8px;
    height: 8px;
  }
  .hero-footer-slide .footer-inner {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  .hero-footer-slide .footer-left {
    order: 2;
  }
  .hero-footer-slide .footer-right {
    order: 1;
    flex-wrap: wrap;
    justify-content: center;
  }
  .hero-footer-slide .qr-box {
    width: 64px;
    height: 64px;
  }
}
</style>
